import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import z from "zod";

import { useSelector } from "@/store";
import { isLocalLvtHost } from "@/utilities/isLocalLvtHost";


type ProductEntitlements = {
    adminAccess: boolean;
    ccAccess: boolean;
    vmAccess: boolean;
    clientCCAccess: boolean;
};

interface ProductEntitlementsContextProps {
    data: ProductEntitlements;
}

const ProductEntitlementsContext = createContext<ProductEntitlementsContextProps | undefined>(undefined);

const ProductEntitlementsSchema = z.object({
    adminAccess: z.boolean(),
    ccAccess: z.boolean(),
    vmAccess: z.boolean(),
    clientCCAccess: z.boolean(),
});

const LocalStorageProductEntitlementsSchema = z.object({
    userId: z.string().uuid(),
    entitlements: ProductEntitlementsSchema,
});

interface ProductEntitlementsProviderProps {
    children: ReactNode;
    nextAppUrl: string;
}

const LOCAL_ENTITLEMENTS: z.infer<typeof ProductEntitlementsSchema> = {
    adminAccess: true,
    ccAccess: true,
    vmAccess: true,
    clientCCAccess: true,
};

export default function ProductEntitlementsProvider({ children, nextAppUrl }: ProductEntitlementsProviderProps) {
    const { lvtu } = useSelector((state) => state.user);
    // we assume that the user has no product entitlements to begin with
    const [data, setData] = useState<z.infer<typeof ProductEntitlementsSchema>>({
        adminAccess: false,
        ccAccess: false,
        vmAccess: false,
        clientCCAccess: false,
    });
    const [isFetchStarted, setIsFetchStarted] = useState(false);

    useEffect(() => {
        if (!lvtu || isFetchStarted) {
            return;
        }

        if (isLocalLvtHost()) {
            setIsFetchStarted(true);
            setData(LOCAL_ENTITLEMENTS);

            return;
        }

        // This function uses the product entitlements value stored in localStorage if it is
        // preset and matches the current user, then ensures an up-to-date value is used by
        // making a request for the entitlements and updates the localStorage value.
        //
        // If the localStorage value is absent or does not correspond to the current user
        // AND the request fails (likely due to the NextJS app's cookie not being present or
        // no longer being valid), then the user is redirected to the NextJS app to log in
        // where they will then get the NextJS app's cookie.
        //
        // Falling back to the localStorage value is a comporomise that allows the user to
        // keep using the site in case the NextJS app's cookie is no longer valid. The
        // possibility that the request fails and the localStorage value is out of date is
        // very low.
        //
        // Why use the value in localStorage first instead of just waiting for the response?
        // This reduces nav icon jitter in the UI. We can more quickly retrieve values from
        // localstorage than retrieve them from an http response and the value in
        // localStorage is very likely to be the value that is retrieved from the NextJS
        // app.
        (async () => {
            setIsFetchStarted(true);

            // First we set the data from localStorage if it is good, and remove it if it is bad
            try {
                const item = localStorage.getItem("product-entitlements");

                if (!item) {
                    throw new Error("product entitlements are not stored in localStorage");
                }

                const anyObject = JSON.parse(item);

                const value = LocalStorageProductEntitlementsSchema.parse(anyObject);

                // ensure value is for the current user
                if (value.userId !== lvtu) {
                    throw new Error("product entitlements in localStorage do not match the current user");
                } else {
                    setData(value.entitlements);
                }
            } catch (e) {
                console.log(`Could not process product entitlements from localStorage: ${e}`);

                // If we get here we assume the value is bad and remove it from localStorage.
                localStorage.removeItem("product-entitlements");
            }

            // We are now sure that the value in localStorage is for the current user and is good, else it is not set

            // Finally, we fetch the up-to-date value from the NextJS app
            try {
                const response = await fetch(`${nextAppUrl}/api/user/product-entitlements`, {
                    credentials: "include",
                });

                if (!response.ok) {
                    throw new Error(`response for product entitlements is not ok: ${response}`);
                }

                const unsafeValue = await response.json();

                const entitlements = ProductEntitlementsSchema.parse(unsafeValue);

                localStorage.setItem("product-entitlements", JSON.stringify({ userId: lvtu, entitlements }));

                setData(entitlements);
            } catch (e) {
                console.log(`unable to request product entitlements: ${e}`);

                if (!localStorage.getItem("product-entitlements")) {
                    // There was a problem requesting product entitlements from the
                    // NextJS app possibly due to the NextJS app's session cookie having
                    // expired, and also we have no record of what the product
                    // entitlements ever were for this user, so here we temporarily
                    // redirect back the the NextJS app so we can get the cookie again
                    console.error(`Error product entitlements request failed: ${e}`);
                    window.location.href = `${nextAppUrl}/external-landing?redirect_to=${encodeURIComponent(
                        window.location.href,
                    )}`;
                }
            }
            // If we got to here, we're good. We at least have a localStorage record
            // of what this user's product entitlements were. The likelihood of
            // the request failing and the record being inaccurate is very low.
        })();
    }, [lvtu]);

    return <ProductEntitlementsContext.Provider value={{ data }}>{children}</ProductEntitlementsContext.Provider>;
}

export function useProductEntitlementsContext() {
    const context = useContext(ProductEntitlementsContext);

    if (!context) {
        throw new Error("useProductEntitlementsContext is not available");
    }

    return context;
}
